/**
 * PAGE: FORM SUBMISSIONS
 * Consultation des réponses à un formulaire
 */

import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button, LoadingState, ErrorState } from '../components/ui';
import { EnterpriseTable } from '../components/tables';
import { EmptyCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import { useEnterpriseTable } from '../hooks/useEnterpriseTable';
import { useFormSubmissions } from '../hooks/useFormSubmissions';
import { useForms } from '../hooks/useForms';
import { useFormFields } from '../hooks/useFormFields';
import type { FormSubmission, FormFieldWithValues } from '../types/form';
import { exportToCSV } from '../lib/csv';

export function FormSubmissionsPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();

  const { forms, loading: formsLoading } = useForms();
  const { fields, loading: fieldsLoading } = useFormFields({ formId, autoLoad: true });
  const { submissions, loading: submissionsLoading } = useFormSubmissions({
    formId,
    autoLoad: true,
  });

  const form = useMemo(() => forms.find((f) => f.id === formId), [forms, formId]);
  const loading = formsLoading || fieldsLoading || submissionsLoading;

  // =========================================================================
  // COLUMNS DYNAMIQUES
  // =========================================================================

  const columns = useMemo<ColumnDef<FormSubmission>[]>(() => {
    const baseColumns: ColumnDef<FormSubmission>[] = [
      {
        accessorKey: 'created_at',
        header: 'Date',
        cell: ({ row }) => {
          const date = new Date(row.original.created_at);
          return (
            <TableCell>
              {`${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
            </TableCell>
          );
        },
      },
    ];

    // Ajouter une colonne par champ du formulaire
    fields.forEach((field: FormFieldWithValues) => {
      baseColumns.push({
        id: field.id,
        header: field.label,
        cell: ({ row }) => {
          const value = row.original.data[field.id];

          if (value === null || value === undefined || value === '') {
            return <EmptyCell value={null} />;
          }

          // Formater selon le type
          if (field.field_type === 'checkbox') {
            return <TableCell>{value ? 'Oui' : 'Non'}</TableCell>;
          }

          if (field.field_type === 'date') {
            try {
              const date = new Date(value);
              return <TableCell>{date.toLocaleDateString('fr-FR')}</TableCell>;
            } catch {
              return <TableCell>{String(value)}</TableCell>;
            }
          }

          return <TableCell>{String(value)}</TableCell>;
        },
      });
    });

    // Colonne contact
    baseColumns.push({
      id: 'contact',
      header: 'Contact',
      cell: ({ row }) => {
        const contact = row.original.contact;
        if (!contact) return <EmptyCell value={null} />;
        return <TableCell>{`${contact.first_name} ${contact.last_name}`}</TableCell>;
      },
    });

    return baseColumns;
  }, [fields]);

  // =========================================================================
  // TABLE
  // =========================================================================

  const table = useEnterpriseTable<FormSubmission>({
    data: submissions,
    searchableFields: ['data'],
    initialPageSize: 10,
    mode: 'client',
  });

  // =========================================================================
  // HANDLERS
  // =========================================================================

  function handleExportCSV() {
    if (submissions.length === 0) return;

    const dataToExport = submissions.map((submission) => {
      const row: Record<string, any> = {
        'Date de soumission': new Date(submission.created_at).toLocaleString('fr-FR'),
      };

      // Ajouter les valeurs de chaque champ
      fields.forEach((field) => {
        const value = submission.data[field.id];

        if (field.field_type === 'checkbox') {
          row[field.label] = value ? 'Oui' : 'Non';
        } else if (field.field_type === 'date' && value) {
          try {
            row[field.label] = new Date(value).toLocaleDateString('fr-FR');
          } catch {
            row[field.label] = value;
          }
        } else {
          row[field.label] = value || '';
        }
      });

      // Ajouter le contact
      if (submission.contact) {
        row['Contact'] = `${submission.contact.first_name} ${submission.contact.last_name}`;
        row['Email contact'] = submission.contact.email;
      }

      return row;
    });

    const fileName = form ? `soumissions-${form.slug}.csv` : 'soumissions.csv';
    exportToCSV(dataToExport, fileName);
  }

  // =========================================================================
  // RENDER
  // =========================================================================

  if (loading) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-8">
        <ErrorState
          title="Formulaire introuvable"
          message="Le formulaire demandé n'existe pas"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 p-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-7 w-7 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900">{form.name}</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {submissions.length} soumission{submissions.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={submissions.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {submissions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-500 text-sm">Aucune soumission pour le moment</p>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            <EnterpriseTable
              data={table.displayedData}
              columns={columns}
              onRowSelectionChange={table.setSelectedItems}
              columnVisibility={table.columnVisibility}
              onColumnVisibilityChange={table.setColumnVisibility}
            />
          </div>
        )}
      </div>

      {/* Pagination */}
      {submissions.length > 0 && (
        <div className="border-t border-zinc-200 p-4 bg-white">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setCurrentPage(table.currentPage - 1)}
              disabled={table.currentPage === 1}
            >
              Précédent
            </Button>

            <p className="text-sm text-zinc-600">
              Page {table.currentPage} sur {table.totalPages}
            </p>

            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setCurrentPage(table.currentPage + 1)}
              disabled={table.currentPage === table.totalPages}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
